import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import ZweiFaktorEinrichtung from '../../ZweiFaktorEinrichtung';
import '../../MandantForm.css';
import '../../BenutzerList.css';   // benutzer-table fuer die Geraete-Liste

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

interface Status {
  methode: 'totp' | 'email' | null;
  aktiviert_am: string | null;
  backup_codes_uebrig: number;
  pflicht: boolean;
  email_versand_moeglich: boolean;
}

interface Geraet {
  geraet_id: number;
  bezeichnung: string | null;
  erstellt_am: string;
  zuletzt_genutzt: string | null;
  ablauf: string;
}

interface Props {
  authToken: string;
}

const datum = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('de-DE') : '—');

/** Eigenes Konto: Passwort, Zwei-Faktor, vertraute Geraete (Spec §6.2). Fuer alle Rollen. */
const KontoSection: React.FC<Props> = ({ authToken }) => {
  const headers = { Authorization: `Bearer ${authToken}` };
  const [status, setStatus] = useState<Status | null>(null);
  const [geraete, setGeraete] = useState<Geraet[]>([]);
  const [einrichten, setEinrichten] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  // Passwort aendern
  const [altes, setAltes] = useState('');
  const [neues, setNeues] = useState('');
  const [neuesWdh, setNeuesWdh] = useState('');

  // Passwortabfrage fuer Backup-Codes/Deaktivieren
  const [aktion, setAktion] = useState<'backup' | 'deaktivieren' | null>(null);
  const [aktionPasswort, setAktionPasswort] = useState('');
  const [neueCodes, setNeueCodes] = useState<string[] | null>(null);
  const meldungTimer = useRef<number | null>(null);

  useEffect(() => () => {
    if (meldungTimer.current) window.clearTimeout(meldungTimer.current);
  }, []);

  const laden = useCallback(async () => {
    try {
      const [s, g] = await Promise.all([
        axios.get(`${API_URL}/api/auth/zwei-faktor/status`, { headers }),
        axios.get(`${API_URL}/api/auth/zwei-faktor/geraete`, { headers }),
      ]);
      setStatus(s.data);
      setGeraete(g.data);
    } catch {
      setFehler('Kontodaten konnten nicht geladen werden.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  useEffect(() => { laden(); }, [laden]);

  const zeige = (text: string) => {
    setMeldung(text);
    setFehler(null);
    if (meldungTimer.current) window.clearTimeout(meldungTimer.current);
    meldungTimer.current = window.setTimeout(() => setMeldung(null), 4000);
  };
  const zeigeFehler = (e: any, fallback: string) => {
    // slowapi antwortet bei 429 mit {"error": ...} statt {"detail": ...}
    const text = e.response?.status === 429
      ? 'Zu viele Versuche. Bitte eine Minute warten.'
      : (e.response?.data?.detail || fallback);
    setFehler(text);
    setMeldung(null);
  };

  const passwortAendern = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (neues !== neuesWdh) { setFehler('Die neuen Passwörter stimmen nicht überein.'); return; }
    try {
      await axios.post(`${API_URL}/api/auth/passwort-aendern`, { altes_passwort: altes, neues_passwort: neues }, { headers });
      setAltes(''); setNeues(''); setNeuesWdh('');
      zeige('Passwort geändert.');
    } catch (e: any) {
      zeigeFehler(e, 'Passwort konnte nicht geändert werden.');
    }
  };

  const aktionAusfuehren = async (ev: React.FormEvent) => {
    ev.preventDefault();
    try {
      if (aktion === 'backup') {
        const r = await axios.post(`${API_URL}/api/auth/zwei-faktor/backup-codes/neu`, { passwort: aktionPasswort }, { headers });
        setNeueCodes(r.data.backup_codes);
      } else if (aktion === 'deaktivieren') {
        await axios.post(`${API_URL}/api/auth/zwei-faktor/deaktivieren`, { passwort: aktionPasswort }, { headers });
        localStorage.removeItem('geraete_token');
        zeige('Zwei-Faktor-Authentifizierung deaktiviert.');
      }
      setAktion(null);
      setAktionPasswort('');
      laden();
    } catch (e: any) {
      zeigeFehler(e, 'Aktion fehlgeschlagen.');
    }
  };

  const geraetEntfernen = async (g: Geraet) => {
    try {
      await axios.delete(`${API_URL}/api/auth/zwei-faktor/geraete/${g.geraet_id}`, { headers });
      laden();
    } catch (e: any) {
      zeigeFehler(e, 'Gerät konnte nicht entfernt werden.');
    }
  };

  if (!status) return <div className="mandant-form">{fehler || 'Lade …'}</div>;

  return (
    <div className="mandant-form konto-section">
      {meldung && <div className="form-group"><p className="form-hint" style={{ color: 'var(--green-900)' }}>{meldung}</p></div>}
      {fehler && <div className="form-error">{fehler}</div>}

      <h3>Passwort ändern</h3>
      <form onSubmit={passwortAendern}>
        <div className="form-group"><label>Aktuelles Passwort</label>
          <input type="password" value={altes} onChange={e => setAltes(e.target.value)} required autoComplete="current-password" /></div>
        <div className="form-group"><label>Neues Passwort (mind. 6 Zeichen)</label>
          <input type="password" value={neues} onChange={e => setNeues(e.target.value)} required minLength={6} autoComplete="new-password" /></div>
        <div className="form-group"><label>Neues Passwort wiederholen</label>
          <input type="password" value={neuesWdh} onChange={e => setNeuesWdh(e.target.value)} required autoComplete="new-password" /></div>
        <div className="form-actions"><button className="btn-save" type="submit">Passwort speichern</button></div>
      </form>

      <h3>Zwei-Faktor-Authentifizierung</h3>
      {status.pflicht && <div className="form-group"><p className="form-hint">Ihr Unternehmen verlangt die Zwei-Faktor-Authentifizierung.</p></div>}
      {einrichten ? (
        <ZweiFaktorEinrichtung token={authToken} tokenArt="voll"
                               onFertig={() => { setEinrichten(false); laden(); zeige('Zwei-Faktor aktiviert.'); }}
                               onAbbrechen={() => setEinrichten(false)} />
      ) : status.methode ? (
        <>
          <p>
            Aktiv seit {datum(status.aktiviert_am)} über {status.methode === 'totp' ? 'Authenticator-App' : 'E-Mail-Code'}.
            Backup-Codes übrig: <strong>{status.backup_codes_uebrig}</strong>
          </p>
          {neueCodes && (
            <div className="form-group">
              <label>Neue Backup-Codes (werden nur jetzt angezeigt)</label>
              <pre style={{ fontFamily: 'ui-monospace, monospace', userSelect: 'all' }}>{neueCodes.join('\n')}</pre>
              <button className="btn-cancel" type="button" onClick={() => setNeueCodes(null)}>Ausblenden</button>
            </div>
          )}
          {aktion ? (
            <form onSubmit={aktionAusfuehren}>
              <div className="form-group">
                <label>{aktion === 'backup' ? 'Passwort bestätigen, um neue Backup-Codes zu erzeugen' : 'Passwort bestätigen, um Zwei-Faktor zu deaktivieren'}</label>
                <input type="password" value={aktionPasswort} onChange={e => setAktionPasswort(e.target.value)} required autoFocus />
              </div>
              <div className="form-actions">
                <button className="btn-save" type="submit">Bestätigen</button>
                <button className="btn-cancel" type="button" onClick={() => { setAktion(null); setAktionPasswort(''); }}>Abbrechen</button>
              </div>
            </form>
          ) : (
            <div className="form-actions">
              <button className="btn-save" type="button" onClick={() => setAktion('backup')}>Backup-Codes neu erzeugen</button>
              {!status.pflicht && (
                <button className="btn-cancel" type="button" onClick={() => setAktion('deaktivieren')}>Deaktivieren</button>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          <p>Nicht aktiv. Schützen Sie Ihr Konto zusätzlich mit einem Code aus einer Authenticator-App oder per E-Mail.</p>
          <div className="form-actions">
            <button className="btn-save" type="button" onClick={() => setEinrichten(true)}>Einrichten</button>
          </div>
        </>
      )}

      <h3>Vertraute Geräte</h3>
      {geraete.length === 0 ? (
        <div className="form-group"><p className="form-hint">Keine vertrauten Geräte. Beim Login mit Code können Sie ein Gerät 30 Tage merken.</p></div>
      ) : (
        <table className="benutzer-table">
          <thead><tr><th>Gerät</th><th>Gemerkt am</th><th>Zuletzt genutzt</th><th>Gültig bis</th><th></th></tr></thead>
          <tbody>
            {geraete.map(g => (
              <tr key={g.geraet_id}>
                <td>{g.bezeichnung || 'Unbekannt'}</td>
                <td>{datum(g.erstellt_am)}</td>
                <td>{datum(g.zuletzt_genutzt)}</td>
                <td>{datum(g.ablauf)}</td>
                <td><button className="btn-cancel" type="button" onClick={() => geraetEntfernen(g)}>Entfernen</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default KontoSection;
